<template>
  <div class="side-menu-container">
    <!-- Expand menu -->
    <div ref="sideMenuExpand" class="side-menu-expand">
      <measurement-menu v-if="activeItem === 'measurement'" />
      <!-- Grid for style -->
      <a-row class="grid-option" v-if="activeItem === 'style'" :gutter="8">
        <option-item
          v-for="(item, index) in options[activeItem]"
          @select-item="selectOption"
          :key="index"
          :item="item"
          :col="cols"
        />
      </a-row>
      <!-- Grid for rim, temple, lens -->
      <div class="grid-option" v-if="['rim', 'temple', 'lens'].includes(activeItem)">
        <div v-for="key in Object.keys(options[activeItem])" :key="key">
          <div style="margin-top: 12px;">{{ key }}</div>
          <a-row :gutter="8">
            <option-item
              v-for="(item, index) in options[activeItem][key]"
              @select-item="selectOption"
              :key="index"
              :item="item"
              :col="cols"
            />
          </a-row>
        </div>
      </div>
      <!-- Collapse button -->
      <div class="btn-collapse" @click="collapseExpandMenu">
        <img src="images/ic_collapse.svg" alt="" />
      </div>
    </div>
    <!-- Main menu -->
    <div class="side-menu-front">
      <menu-item v-for="item in menuItems" @select-item="setActiveItem" :key="item.photo" :item="item" />
      <div ref="indicator" class="indicator" />
    </div>
  </div>
</template>

<script>
import { morph } from "quasar";
import MenuItem from "./MenuItem.vue";
import OptionItem from "./OptionItem.vue";
import MeasurementMenu from "./MeasurementMenu.vue";

const ITEM_HEIGHT_MULTIPLIER = n => 90 * n;
const defaultAnimConfig = {
  duration: 300,
  tween: true
};

export default {
  components: {
    MenuItem,
    OptionItem,
    MeasurementMenu
  },
  props: ["screenWidth"],
  data: function() {
    return {
      activeItem: "",
      menuItems: [
        { photo: "images/menu_style.svg", title: "Style", key: "style" },
        { photo: "images/menu_texture.svg", title: "Texture", key: "rim" },
        { photo: "images/menu_temple.svg", title: "Temples", key: "temple" },
        { photo: "images/menu_lenses.svg", title: "Lenses color", key: "lens" },
        { photo: "images/menu_measurement.svg", title: "Measurement", key: "measurement" }
      ]
    };
  },
  mounted() {
    if (navigator.appVersion.indexOf("Win") >= 0) {
      this.$refs.sideMenuExpand.classList.add("windows-only");
    }
  },
  methods: {
    selectOption: function(item) {
      this.$store.commit("selectOption", item);
    },
    setActiveItem: function(item) {
      const currentItem = this.activeItem;
      const onToggle = () => {
        const activeIndex = this.menuItems.findIndex(({ key }) => key === item);
        this.$refs.indicator.style.top = 10 + ITEM_HEIGHT_MULTIPLIER(activeIndex) + "px";
        this.$refs.indicator.style.display = "block";
      };
      // Animate indicator
      morph({
        ...defaultAnimConfig,
        from: this.$refs.indicator,
        onToggle,
        duration: currentItem ? 500 : 0
      });
      this.activeItem = item;
      // Animate side menu expand
      morph({
        ...defaultAnimConfig,
        from: this.$refs.sideMenuExpand,
        onToggle: () => {
          this.$refs.sideMenuExpand.classList.add("side-menu-expand-opened");
        }
      });
    },
    collapseExpandMenu: function() {
      this.$refs.indicator.style.display = "none";
      this.activeItem = "";
      // Animate side menu expand
      morph({
        ...defaultAnimConfig,
        from: this.$refs.sideMenuExpand,
        onToggle: () => {
          this.$refs.sideMenuExpand.classList.remove("side-menu-expand-opened");
        }
      });
    }
  },
  computed: {
    options() {
      const options = Object.assign({}, this.$store.state.options);

      ["rim", "temple", "lens"].forEach(glassesPart => {
        const group = options[glassesPart].reduce((prev, option) => {
          let groupName = option.sku.split("-")[0];
          if (glassesPart === "lens") {
            groupName = option.sku.indexOf("Mirror") >= 0 ? "Mirror lens" : "Clear lens";
          }

          return {
            ...prev,
            [groupName]: prev[groupName]?.concat(option) || [option]
          };
        }, {});
        options[glassesPart] = group;
      });

      return options;
    },
    cols() {
      return this.screenWidth >= 576 ? 2 : 1;
    }
  }
};
</script>

<style lang="scss" scoped>
.side-menu {
  &-container {
    width: 100%;
    height: 100%;
    position: relative;
  }

  &-expand {
    position: absolute;
    left: 0;
    width: 100%;
    height: 100%;
    padding-left: 30px;
    background-color: rgb(233, 250, 254);

    @media screen and (max-width: 575px) {
      padding-left: 20px;
      background-color: rgba(233, 250, 254, 0.9);
    }

    .grid-option {
      width: 100%;
      height: 100%;
      overflow-x: hidden;
      overflow-y: auto;
    }

    &.windows-only {
      .grid-option {
        &::-webkit-scrollbar-track {
          -webkit-box-shadow: inset 0 0 3px rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          background-color: #f5f5f5;
        }

        &::-webkit-scrollbar {
          width: 8px;
          background-color: #f5f5f5;
        }

        &::-webkit-scrollbar-thumb {
          -webkit-box-shadow: inset 0 0 3px rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          background-color: #999999;
        }
      }
    }

    .btn-collapse {
      position: absolute;
      top: 50%;
      left: 5px;
      transform: translateY(-50%);
      cursor: pointer;

      img {
        width: 16px;
      }
    }
  }

  &-expand-opened {
    left: -260px;
    width: 260px;

    @media screen and (max-width: 575px) {
      left: -120px;
      width: 120px;
    }
  }

  &-front {
    width: 100%;
    height: 100%;
    background-color: rgb(233, 250, 254);
    position: relative;
    z-index: 2;

    .indicator {
      width: 0;
      height: 70px;
      background-color: #2f80ed;
      border: 2px solid #2f80ed;
      border-radius: 35px;
      box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
      position: absolute;
      display: none;
      z-index: 3;
    }
  }
}
</style>
